import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import {
    Button,
    Flex,
    FormControl,
    FormLabel,
    Input,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Select,
    Text,
    useColorModeValue,
    Textarea,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import { ROUTES } from "../../../constants/routes-path";
import { parametersService } from "services/parametersService";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import Loading from "components/Loading/Loading";

function ParametersEdit() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams();
    const toast = useCustomToast();
    const textColor = useColorModeValue("gray.700", "white");
    const bgTextarea = useColorModeValue("white", "navy.900");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");
    const [selectedParameter, setSelectedParameter] = useState(null);

    const fetchParameter = async (id) => {
        try {
            const parameter = await parametersService.find(id);
            setSelectedParameter(parameter);
        } catch (error) {
            console.error(t('system.messages.errors.parameter.list'), error);
        }
    };

    useEffect(() => {
        if (id) {
            fetchParameter(id);
        }
    }, [id]);

    const [code, setCode] = useState("");
    const [description, setDescription] = useState("");
    const [value, setValue] = useState("");

    const handleUpdateParameter = async () => {
        if (!selectedParameter) return;

        try {
            const updatedParameter = {
                ...selectedParameter,
                valor:
                selectedParameter.tipo === t("settings.parameter.value.type.integer")
                        ? parseInt(value, 10) || 0
                        : selectedParameter.tipo === t("settings.parameter.value.type.boolean")
                            ? value === "1"
                            : value
            };

            await parametersService.update(selectedParameter.id, updatedParameter);

            toast({
                title: t("settings.parameter.messages.success.edit"),
                status: "success",
            });

            navigate(ROUTES.parametersPath);
        } catch (error) {
            console.error(error);

            const errorMessage = t(error.message);

            toast({
                title: t("system.notifications.error"),
                description: errorMessage,
                status: "error",
                duration: 5000,
                isClosable: true,
                position: "top-right",
            });
        }
    };

    useEffect(() => {
        if (selectedParameter) {
            setCode(selectedParameter.codigo || "");
            setDescription(selectedParameter.descricao || "");
            setValue(selectedParameter.valor || "");
        }
    }, [selectedParameter]);

    const handleValueChange = (event) => {
        let newValue = event.target.value;

        if (selectedParameter?.tipo === t("settings.parameter.value.type.boolean")) {
            newValue = newValue === "1" ? "1" : "0";
        } else if (selectedParameter?.tipo === t("settings.parameter.value.type.integer")) {
            newValue = newValue.replace(/\D/g, "");
        }

        setValue(newValue);
    };

    const renderValueInput = () => {
        if (selectedParameter?.tipo === t("settings.parameter.value.type.boolean")) {
            return (
                <Select
                    variant="main"
                    fontSize="xs"
                    value={value}
                    onChange={handleValueChange}
                >
                    <option value="1">{t("system.labels.true")}</option>
                    <option value="0">{t("system.labels.false")}</option>
                </Select>
            );
        }

        if (selectedParameter?.tipo === t("settings.parameter.value.type.integer")) {
            return (
                <NumberInput
                    value={value}
                    onChange={(val) => setValue(val)}
                    variant="main"
                    fontSize="xs"
                    allowMouseWheel
                >
                    <NumberInputField placeholder={t("settings.parameter.form.fields.value.placeholder")} />
                    <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                    </NumberInputStepper>
                </NumberInput>
            );
        }

        return (
            <Textarea
                bg={bgTextarea}
                value={value}
                _placeholder={{ color: placeholderColor }}
                placeholder={t("settings.parameter.form.fields.value.placeholder")}
                fontSize="xs"
                onChange={handleValueChange}
                required
            />
        );
    };

    return (
        selectedParameter ? (
            <Flex direction="column" minH="89vh" align="center" pt={{ sm: "120px", md: "75px" }}>
                <Card mt={"80px"}>
                    <CardHeader mb="40px" border={`1px solid ${useColorModeValue("#EDF2F7","#A0AEC0")}`} padding="1em" borderRadius="lg">
                        <Text color={textColor} fontSize="lg" fontWeight="bold" mb="3px">
                            {t('settings.parameter.edit.title')}
                        </Text>
                        <Text color="gray.400" fontWeight="normal" fontSize="sm">
                            {t('settings.parameter.edit.subtitle')}
                        </Text>
                    </CardHeader>
                    <CardBody>
                        <FormControl isRequired>
                            <FormLabel
                                color={textColor}
                                fontWeight="bold"
                                fontSize="xs"
                            >
                                {t("settings.parameter.form.fields.code.label")}
                            </FormLabel>
                            <Input
                                variant="main"
                                value={code}
                                disabled={true}
                                placeholder={t("settings.parameter.form.fields.code.placeholder")}
                                fontSize="xs"
                                required
                            />
                        </FormControl>

                        <FormControl isRequired mt="4">
                            <FormLabel
                                color={textColor}
                                fontWeight="bold"
                                fontSize="xs"
                            >
                                {t("settings.parameter.form.fields.description.label")}
                            </FormLabel>
                            <Input
                                variant="main"
                                value={description}
                                disabled={true}
                                placeholder={t("settings.parameter.form.fields.description.placeholder")}
                                fontSize="xs"
                                required
                            />
                        </FormControl>

                        <FormControl mt="4">
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("settings.parameter.form.fields.value.label")}
                            </FormLabel>
                            {renderValueInput()}
                        </FormControl>

                        <Flex justify="space-between">
                            <Button
                                variant="light"
                                alignSelf="flex-end"
                                mt="24px"
                                w="125px"
                                h="40px"
                                fontSize="sm"
                                onClick={() => navigate(ROUTES.parametersPath)}
                            >
                                {t(`system.actions.cancel`)}
                            </Button>
                            <Button
                                variant="dark"
                                alignSelf="flex-end"
                                type="submit"
                                mt="24px"
                                w="125px"
                                h="40px"
                                fontSize="sm"
                                _hover={{
                                    bg: "grey.500",
                                    color: "white"
                                }}
                                onClick={handleUpdateParameter}
                            >
                                {t(`system.actions.save`)}
                            </Button>
                        </Flex>
                    </CardBody>
                </Card>
            </Flex>
        ) : (
            <Loading />
        )
    );

}

export default ParametersEdit;